import type { EraDef } from '../types';

/**
 * ERA 2 — CAMBRIAN SEAS
 * Movement identity: everything strafes, jets and leaves ink behind. Squids
 * and the apex elite teleport (`blink` — Era 2's signature behavior) instead
 * of walking, and most of the roster trails a low-dps acid/ink hazard
 * (`EnemyDef.trail` — Era 2's exclusive hazard theme) while they move.
 * Pattern identity: fast walls with gaps + needle volleys, curving bullets,
 * and a spiral out of the orbiting trilobite tank.
 * Boss: Anomalocaris Prime — tide-surge `pull` in phase 2 (dragging you into
 * the wall's gaps), plus `blink` + a big `onEnter` needle radial in phase 3.
 * Palette: deep blue/indigo water, bioluminescent teal/orange accents.
 * Enemy bullets: warm orange/red family (contrasts with cyan player bullets).
 */
const ENEMY_BULLET = 0xff7a3c;
const HAZARD = 0xffd23c;
const INK = 0x8a3fd6; // acid/ink trail hazard color — Era 2's exclusive trail theme

export const ERA_CAMBRIAN: EraDef = {
  index: 1,
  id: 'cambrian',
  name: 'Cambrian Seas',
  subtitle: '520 million years ago',
  implemented: true,
  palette: {
    sky: 0x050c26,
    fog: 0x0a1740,
    fogDensity: 0.02,
    ground: 0x0d1f4d,
    groundAlt: 0x142c66,
    ambient: 0x1f3f8a,
    sun: 0x6fe3ff,
    sunIntensity: 1.2,
    accent: 0x2ce8c8,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'reef',
    arenaRadius: 64,
    groundStyle: 'ripples',
    props: [
      { visual: { geo: 'spike', color: 0xff8a4a, emissive: 0xff8a4a, emissiveIntensity: 0.7, scale: 1.1 }, count: 34, scale: [0.5, 1.6], y: 0 },
      { visual: { geo: 'capsule', color: 0x1c8f7a, scaleY: 4, anim: 'wobble' }, count: 28, scale: [0.25, 0.6], y: 1 },
      { visual: { geo: 'sphere', color: 0x4be8ff, emissive: 0x4be8ff, emissiveIntensity: 1.3, anim: 'bob' }, count: 70, scale: [0.06, 0.18], y: 1.4 },
    ],
  },
  enemies: [
    {
      // Swarmer filler: sprints straight at you, dies in a hit or two — counterplay is just kill priority.
      id: 'c2_pikaia',
      name: 'Pikaia',
      archetype: 'swarmer',
      hp: 14,
      speed: 3.8,
      damage: 8,
      radius: 0.4,
      xp: 5,
      visual: { geo: 'capsule', color: 0x8ad0ff, emissive: 0x1d4a7a, emissiveIntensity: 0.5, scale: 0.9, scaleY: 1.4, anim: 'wobble' },
      behavior: { type: 'chase' },
    },
    {
      // Filler drifter lobbing slow orbs — harmless at range, an easy kill if you close the distance.
      id: 'c2_jelly',
      name: 'Drift Jelly',
      archetype: 'shooter',
      hp: 32,
      speed: 1.6,
      damage: 8,
      radius: 0.6,
      xp: 7,
      visual: { geo: 'blob', color: 0x2ce8c8, emissive: 0x2ce8c8, emissiveIntensity: 1, scale: 1.1, anim: 'pulse' },
      behavior: { type: 'drift', jitter: 0.5 },
      patterns: ['c2_radial_slow'],
      patternCooldown: 2.4,
    },
    {
      // Signature: teleports to a new angle ~8u out every 3s (invuln flash on arrival = telegraph), fires a curving
      // burst the instant it lands — punish the flash, don't chase the kite.
      id: 'c2_squid',
      name: 'Jet Squid',
      archetype: 'shooter',
      hp: 36,
      speed: 2.4,
      damage: 10,
      radius: 0.5,
      xp: 9,
      shardChance: 0.08,
      visual: { geo: 'cone', color: 0x5ad6ff, emissive: 0x0c3a5c, emissiveIntensity: 0.9, scale: 1, scaleY: 1.3, anim: 'bob' },
      behavior: { type: 'blink', interval: 3.2, range: 8, distance: 8 },
      patterns: ['c2_curve_pair', 'c2_burst_split'],
      patternCooldown: 1.1,
      fireRange: 20,
    },
    {
      // Tank: orbits at mid-range spitting a spiral, and leaves an ink slick as it circles — approach from outside
      // the ring, don't cross its own trail.
      id: 'c2_trilobite',
      name: 'Trilobite',
      archetype: 'tank',
      hp: 150,
      speed: 1.6,
      damage: 12,
      radius: 0.75,
      xp: 11,
      shardChance: 0.12,
      visual: { geo: 'disc', color: 0x3a6bb0, emissive: 0x14284f, emissiveIntensity: 0.4, scale: 1.3 },
      behavior: { type: 'orbit', radius: 8, angular: 0.35 },
      patterns: ['c2_spiral'],
      patternCooldown: 1.6,
      fireRange: 18,
      trail: { radius: 0.9, duration: 2, dps: 5, interval: 0.5, color: INK },
    },
    {
      // Telegraphed rush (windup then a fast charge); leaves a short ink trail along its charge lane so you can see
      // where not to stand right after it passes.
      id: 'c2_anomalocaris',
      name: 'Anomalocaris',
      archetype: 'charger',
      hp: 100,
      speed: 2.6,
      damage: 19,
      radius: 0.95,
      xp: 15,
      visual: { geo: 'cone', color: 0xffa24a, emissive: 0x7a4610, emissiveIntensity: 0.5, scale: 1.3, scaleY: 1.5 },
      behavior: { type: 'charge', windup: 0.8, speedMult: 4.6, duration: 0.6, cooldown: 1.6 },
      trail: { radius: 0.7, duration: 1.4, dps: 4, interval: 0.4, color: INK },
    },
    {
      // Needle-volley shooter: holds range and alternates a gapped bullet wall with an aimed needle spray — dodge
      // through the wall's gaps, then close in during the reload.
      id: 'c2_hallucigenia',
      name: 'Hallucigenia',
      archetype: 'shooter',
      hp: 42,
      speed: 2,
      damage: 10,
      radius: 0.55,
      xp: 9,
      visual: { geo: 'spike', color: 0xd0507a, emissive: 0x5a1633, emissiveIntensity: 0.6, scale: 1, anim: 'wobble' },
      behavior: { type: 'keepDistance', distance: 10, strafe: 0.3 },
      patterns: ['c2_wall', 'c2_aimed_volley'],
      patternCooldown: 1.7,
      fireRange: 17,
    },
    {
      // Elite: blinks further out than the squid, opens on arrival with a wall+curve combo, and trails ink the
      // whole time it's up — the rarest, most dangerous kiter in the era.
      id: 'c2_opabinia',
      name: 'Opabinia',
      archetype: 'elite',
      hp: 135,
      speed: 2.8,
      damage: 15,
      radius: 0.8,
      xp: 26,
      shardChance: 0.4,
      elite: true,
      visual: { geo: 'octa', color: 0xff5c9c, emissive: 0xff5c9c, emissiveIntensity: 1, scale: 1.3, anim: 'spin' },
      behavior: { type: 'blink', interval: 4.5, range: 11, distance: 11 },
      patterns: ['c2_wall', 'c2_curve_pair'],
      patternCooldown: 1.2,
      fireRange: 22,
      trail: { radius: 1, duration: 2.2, dps: 6, interval: 0.45, color: INK },
    },
  ],
  patterns: [
    {
      id: 'c2_aimed_volley',
      emitters: [{ shape: 'aimed', interval: 0.13, bursts: 4, count: 1, spread: 0.2, bullet: { speed: 7, lifetime: 3, radius: 0.22, damage: 10, visual: 'needle', color: ENEMY_BULLET } }],
    },
    {
      id: 'c2_radial_slow',
      emitters: [{ shape: 'radial', interval: 1.2, bursts: 1, count: 8, bullet: { speed: 3, lifetime: 5, radius: 0.26, damage: 8, visual: 'orb', color: ENEMY_BULLET } }],
    },
    {
      id: 'c2_spiral',
      duration: 2.6,
      emitters: [{ shape: 'spiral', interval: 0.08, bursts: 0, count: 2, rotatePerBurst: 0.28, bullet: { speed: 5.8, lifetime: 3.6, radius: 0.22, damage: 9, visual: 'shard', color: ENEMY_BULLET } }],
    },
    {
      id: 'c2_wall',
      emitters: [{ shape: 'wall', interval: 0.55, bursts: 2, count: 12, spread: 10, gaps: 3, bullet: { speed: 4.4, lifetime: 4, radius: 0.28, damage: 11, visual: 'ring', color: ENEMY_BULLET } }],
    },
    {
      id: 'c2_curve_pair',
      emitters: [
        { shape: 'aimed', interval: 0.5, bursts: 3, count: 1, angle: 0.15, bullet: { speed: 6, curve: 1.4, lifetime: 3.5, radius: 0.22, damage: 9, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'aimed', interval: 0.5, bursts: 3, count: 1, angle: -0.15, bullet: { speed: 6, curve: -1.4, lifetime: 3.5, radius: 0.22, damage: 9, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c2_burst_split',
      emitters: [
        { shape: 'aimed', interval: 0.9, bursts: 2, count: 1, bullet: { speed: 7, lifetime: 1.4, radius: 0.28, damage: 10, visual: 'orb', color: ENEMY_BULLET, onExpire: 'c2_split_burst' } },
      ],
    },
    {
      id: 'c2_split_burst',
      emitters: [{ shape: 'radial', interval: 1, bursts: 1, count: 6, bullet: { speed: 3.5, lifetime: 1.6, radius: 0.18, damage: 6, visual: 'orb', color: ENEMY_BULLET } }],
    },
    // ---- boss patterns ----
    {
      id: 'c2_boss_ring',
      emitters: [
        { shape: 'radial', interval: 0.65, bursts: 3, count: 20, rotatePerBurst: 0.16, bullet: { speed: 4.4, lifetime: 5, radius: 0.28, damage: 12, visual: 'orb', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c2_boss_double_spiral',
      duration: 3.8,
      emitters: [
        { shape: 'spiral', interval: 0.07, bursts: 0, count: 3, rotatePerBurst: 0.24, bullet: { speed: 5.4, lifetime: 4, radius: 0.24, damage: 10, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'spiral', interval: 0.07, bursts: 0, count: 3, rotatePerBurst: -0.24, angle: 0.5, bullet: { speed: 5.4, lifetime: 4, radius: 0.24, damage: 10, visual: 'shard', color: 0xffb27a } },
      ],
    },
    {
      id: 'c2_boss_homing',
      emitters: [
        { shape: 'radial', interval: 0.42, bursts: 4, count: 5, angle: 0.4, bullet: { speed: 3.6, lifetime: 5, radius: 0.32, damage: 13, homing: 1.7, homingDelay: 0.6, visual: 'blob', color: 0xff5c5c } },
      ],
    },
    {
      id: 'c2_boss_wave_wall',
      emitters: [
        { shape: 'wall', interval: 0.95, bursts: 4, count: 17, spread: 16, gaps: 4, bullet: { speed: 4.2, lifetime: 5, radius: 0.3, damage: 12, wave: { amp: 1.6, freq: 1.1 }, visual: 'ring', color: ENEMY_BULLET } },
      ],
    },
    {
      id: 'c2_boss_hazard_rain',
      emitters: [
        { shape: 'random', interval: 0.25, bursts: 9, count: 3, spread: 6.283, bullet: { speed: 6, accel: -3, minSpeed: 0.5, lifetime: 2, radius: 0.3, damage: 9, visual: 'blob', color: HAZARD, linger: { radius: 1.3, duration: 4, dps: 9, color: HAZARD } } },
      ],
    },
    {
      id: 'c2_boss_split_barrage',
      emitters: [
        { shape: 'aimed', interval: 0.8, bursts: 5, count: 3, spread: 0.5, bullet: { speed: 7.5, lifetime: 1.5, radius: 0.3, damage: 11, visual: 'orb', color: ENEMY_BULLET, onExpire: 'c2_split_burst' } },
      ],
    },
    {
      // Big one-shot needle radial — fired once as the phase-3 onEnter burst, right after the invuln flash ends.
      id: 'c2_boss_needle_radial',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 30, bullet: { speed: 8, lifetime: 3.2, radius: 0.2, damage: 13, visual: 'needle', color: ENEMY_BULLET } },
      ],
    },
  ],
  boss: {
    id: 'c2_anomalocaris_prime',
    name: 'Anomalocaris Prime',
    title: 'Apex of the Cambrian Seas',
    hp: 2400,
    radius: 2.6,
    speed: 1.6,
    damage: 24,
    xp: 220,
    visual: { geo: 'cone', color: 0xff8a3c, emissive: 0x7a3a10, emissiveIntensity: 0.8, scale: 1.6, scaleY: 1.3, anim: 'pulse' },
    phases: [
      {
        // Teaching phase: no special yet, just its two base patterns, with the ring pattern echoed once as an
        // onEnter burst right after the spawn telegraph.
        hpBelow: 1,
        name: 'Hunt',
        behavior: { type: 'keepDistance', distance: 11, strafe: 0.28 },
        patterns: ['c2_boss_ring', 'c2_boss_wave_wall'],
        patternCooldown: 1.6,
        transition: 1.5,
        onEnter: 'c2_boss_ring',
      },
      {
        // Tide surge: every 9s the boss drags players in for 2.2s (screen shake telegraphs the pull) while cycling
        // its gapped wall pattern — the pull hauls you toward the boss, and the wall's gaps become your only outs.
        hpBelow: 0.6,
        name: 'Frenzy',
        behavior: { type: 'chase' },
        patterns: ['c2_boss_wave_wall', 'c2_boss_homing'],
        patternCooldown: 1.15,
        summons: { enemy: 'c2_pikaia', count: 5, interval: 6 },
        transition: 2,
        speedMult: 1.35,
        special: { type: 'pull', interval: 9, duration: 2.2, strength: 5, radius: 22 },
      },
      {
        // Deep Rage: pull returns on a shorter fuse, and the boss itself now jets around the arena (blink) between
        // hazard barrages; a big needle radial fires once as the phase opens.
        hpBelow: 0.3,
        name: 'Deep Rage',
        behavior: { type: 'blink', interval: 6, range: 9, distance: 9 },
        patterns: ['c2_boss_hazard_rain', 'c2_boss_split_barrage', 'c2_boss_double_spiral'],
        patternCooldown: 0.75,
        summons: { enemy: 'c2_jelly', count: 3, interval: 5 },
        transition: 2,
        speedMult: 1.65,
        onEnter: 'c2_boss_needle_radial',
        special: { type: 'pull', interval: 5, duration: 1.6, strength: 6, radius: 20 },
      },
    ],
  },
  stages: [
    {
      name: 'Shallow Reef',
      duration: 200,
      objective: { type: 'collect', count: 45 },
      density: { start: 0.9, end: 2.4 },
      pool: [
        { enemy: 'c2_pikaia', weight: 10 },
        { enemy: 'c2_jelly', weight: 2, minTime: 25 },
        { enemy: 'c2_squid', weight: 3, minTime: 30 },
        { enemy: 'c2_hallucigenia', weight: 4, minTime: 40 },
        { enemy: 'c2_trilobite', weight: 3, minTime: 70 },
      ],
      waves: [
        { t: 4, enemy: 'c2_pikaia', count: 6, formation: 'ring' },
        { t: 28, enemy: 'c2_hallucigenia', count: 4, formation: 'line' },
        { t: 55, enemy: 'c2_jelly', count: 5, formation: 'cluster' },
        { t: 85, enemy: 'c2_trilobite', count: 3, formation: 'edge' },
        { t: 100, enemy: 'c2_squid', count: 3, formation: 'random' },
        { t: 115, enemy: 'c2_anomalocaris', count: 2, formation: 'edge' },
        { t: 150, enemy: 'c2_pikaia', count: 22, formation: 'ring' },
        { t: 180, enemy: 'c2_opabinia', count: 1, formation: 'edge', elite: true },
      ],
    },
    {
      name: 'Abyssal Trench',
      duration: 220,
      density: { start: 1.3, end: 3.0 },
      pool: [
        { enemy: 'c2_pikaia', weight: 6 },
        { enemy: 'c2_hallucigenia', weight: 4 },
        { enemy: 'c2_jelly', weight: 3 },
        { enemy: 'c2_squid', weight: 4, minTime: 15 },
        { enemy: 'c2_trilobite', weight: 4, minTime: 20 },
        { enemy: 'c2_anomalocaris', weight: 3, minTime: 45 },
      ],
      waves: [
        { t: 5, enemy: 'c2_trilobite', count: 4, formation: 'ring' },
        { t: 20, enemy: 'c2_squid', count: 4, formation: 'random' },
        { t: 35, enemy: 'c2_anomalocaris', count: 3, formation: 'line' },
        { t: 65, enemy: 'c2_hallucigenia', count: 6, formation: 'ring' },
        { t: 95, enemy: 'c2_jelly', count: 6, formation: 'cluster' },
        { t: 130, enemy: 'c2_opabinia', count: 1, formation: 'edge', elite: true },
        { t: 160, enemy: 'c2_pikaia', count: 28, formation: 'ring' },
        { t: 190, enemy: 'c2_trilobite', count: 4, formation: 'cluster' },
        { t: 205, enemy: 'c2_opabinia', count: 2, formation: 'line', elite: true },
      ],
    },
    {
      name: 'Anomalocaris Prime',
      duration: 600,
      density: { start: 1.2, end: 1.2 },
      pool: [
        { enemy: 'c2_pikaia', weight: 6 },
        { enemy: 'c2_hallucigenia', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 'c2_pikaia', count: 10, formation: 'ring' }],
      boss: { id: 'c2_anomalocaris_prime', at: 18 },
    },
  ],
  loot: [
    { itemId: 'w_bone_spear', weight: 1 },
    { itemId: 'a_chitin', weight: 1 },
    { itemId: 'r_trilobite_eye', weight: 1 },
  ],
  audio: { music: 'music/era2_sea', ambient: 'amb/deep_water', sfxSet: 'organic' },
};
